import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, DollarSign, TrendingUp, Users, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config';

const PaymentsManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في تحميل المعاملات' : 'Error loading payments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-green-100 text-green-800', label: { en: 'Paid', ar: 'مدفوع', sv: 'Betald' } },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: { en: 'Pending', ar: 'معلق', sv: 'Väntande' } },
      failed: { color: 'bg-red-100 text-red-800', label: { en: 'Failed', ar: 'فشل', sv: 'Misslyckades' } },
      expired: { color: 'bg-gray-100 text-gray-800', label: { en: 'Expired', ar: 'منتهي', sv: 'Utgånget' } }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.color}>
        {config.label[i18n.language] || config.label.en}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'sv' ? 'sv-SE' : 'en-US');
  };

  const formatAmount = (amount, currency) => {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  };

  const stats = {
    total: Array.isArray(transactions) ? transactions.length : 0,
    paid: Array.isArray(transactions) ? transactions.filter(t => t.payment_status === 'paid').length : 0,
    pending: Array.isArray(transactions) ? transactions.filter(t => t.payment_status === 'pending').length : 0,
    totalRevenue: Array.isArray(transactions) 
      ? transactions
          .filter(t => t.payment_status === 'paid')
          .reduce((sum, t) => sum + (t.amount || 0), 0)
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          {i18n.language === 'ar' ? '💳 إدارة المدفوعات' : i18n.language === 'sv' ? '💳 Betalningshantering' : '💳 Payments Management'}
        </h2>
        <p className="text-slate-600 mt-1">
          {i18n.language === 'ar' ? 'عرض وتتبع جميع المعاملات المالية' : i18n.language === 'sv' ? 'Visa och spåra alla betalningar' : 'View and track all payment transactions'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">
                {i18n.language === 'ar' ? 'إجمالي المعاملات' : i18n.language === 'sv' ? 'Totalt antal' : 'Total Transactions'}
              </p>
              <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
            </div>
            <CreditCard className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">
                {i18n.language === 'ar' ? 'مدفوع' : i18n.language === 'sv' ? 'Betalda' : 'Paid'}
              </p>
              <p className="text-3xl font-bold text-green-900">{stats.paid}</p>
            </div>
            <DollarSign className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">
                {i18n.language === 'ar' ? 'معلق' : i18n.language === 'sv' ? 'Väntande' : 'Pending'}
              </p>
              <p className="text-3xl font-bold text-yellow-900">{stats.pending}</p>
            </div>
            <Users className="w-10 h-10 text-yellow-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-600 font-medium">
                {i18n.language === 'ar' ? 'الإيرادات' : i18n.language === 'sv' ? 'Intäkter' : 'Revenue'}
              </p>
              <p className="text-2xl font-bold text-emerald-900">{stats.totalRevenue.toFixed(2)} kr</p>
            </div>
            <TrendingUp className="w-10 h-10 text-emerald-500 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
        </div>
      ) : !Array.isArray(transactions) || transactions.length === 0 ? (
        <Card className="p-12 text-center">
          <CreditCard className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">
            {i18n.language === 'ar' ? 'لا توجد معاملات بعد' : i18n.language === 'sv' ? 'Inga transaktioner ännu' : 'No transactions yet'}
          </p>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {i18n.language === 'ar' ? 'التاريخ' : i18n.language === 'sv' ? 'Datum' : 'Date'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {i18n.language === 'ar' ? 'المستخدم' : i18n.language === 'sv' ? 'Användare' : 'User'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {i18n.language === 'ar' ? 'الخطة' : i18n.language === 'sv' ? 'Plan' : 'Plan'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {i18n.language === 'ar' ? 'المبلغ' : i18n.language === 'sv' ? 'Belopp' : 'Amount'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {i18n.language === 'ar' ? 'الحالة' : i18n.language === 'sv' ? 'Status' : 'Status'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">
                      {transaction.user_email || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">
                      {transaction.plan_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {formatAmount(transaction.amount, transaction.currency)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(transaction.payment_status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PaymentsManagement;
